/**
 * Per-gym persistence for the send-date quick picker in the send dialog.
 *
 * Logging a whole gym session after the fact means picking the same non-today
 * date over and over. To speed that up we remember, scoped per location (gym /
 * crag / competition slug):
 *
 *  - `lastDate`: the last send date confirmed at this location ('yyyy-MM-dd'),
 *    surfaced as a one-tap "reuse" chip in the dialog.
 *  - `defaultMode`: which quick option ('yesterday' | 'lastDate') the user last
 *    picked. When set, the next fresh send dialog for this location pre-fills
 *    the date accordingly instead of defaulting to today.
 *
 * Stored in localStorage; all access is defensive so a disabled/erroring store
 * degrades to "no preference" rather than throwing.
 */

export type SendDateMode = 'yesterday' | 'lastDate';

export interface SendDatePreference {
  /** Last confirmed send date at this location, as 'yyyy-MM-dd', or null. */
  lastDate: string | null;
  /** Quick option to auto-apply when opening a fresh send dialog, or null. */
  defaultMode: SendDateMode | null;
}

const STORAGE_PREFIX = 'climb_log.send_date_pref.v1:';

const EMPTY: SendDatePreference = { lastDate: null, defaultMode: null };

function keyFor(scope: string): string {
  return `${STORAGE_PREFIX}${scope}`;
}

export function getSendDatePreference(scope: string | null | undefined): SendDatePreference {
  if (typeof window === 'undefined' || !scope) return EMPTY;
  try {
    const raw = window.localStorage.getItem(keyFor(scope));
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw) as Partial<SendDatePreference>;
    return {
      lastDate: typeof parsed.lastDate === 'string' ? parsed.lastDate : null,
      defaultMode:
        parsed.defaultMode === 'yesterday' || parsed.defaultMode === 'lastDate'
          ? parsed.defaultMode
          : null,
    };
  } catch {
    return EMPTY;
  }
}

function write(scope: string, pref: SendDatePreference): void {
  if (typeof window === 'undefined' || !scope) return;
  try {
    window.localStorage.setItem(keyFor(scope), JSON.stringify(pref));
  } catch {
    /* non-fatal: storage unavailable/full */
  }
}

/** Remember which quick option is the default for the next entries at this location. */
export function setSendDateDefaultMode(scope: string | null | undefined, mode: SendDateMode): void {
  if (!scope) return;
  write(scope, { ...getSendDatePreference(scope), defaultMode: mode });
}

/** Record the date the user just confirmed a send with at this location. */
export function setLastSendDate(scope: string | null | undefined, date: string): void {
  if (!scope) return;
  write(scope, { ...getSendDatePreference(scope), lastDate: date });
}
